/**
 * TEST-005: record-replay (cassette) provider.
 *
 * The scripted provider verifies the deterministic machinery but ignores the prompt and fakes the
 * model's tool-use. A cassette captures a REAL model's behaviour once (real prompts in, real
 * responses + tool-use out) and replays it deterministically — testing the actual prompts and the
 * model's real decisions in CI at zero per-run cost.
 *
 * - `createRecordingProvider({ provider, cassettePath, … })` wraps a real provider and writes each
 *   interaction to the cassette. Used in a one-off record run (needs an API key).
 * - `createReplayProvider({ cassettePath, … })` replays the recorded responses in order, with
 *   staleness detection: if the incoming request diverges from the recorded one (e.g. a prompt
 *   changed), it fails with a clear "re-record" error instead of returning a wrong response.
 *
 * Volatile substrings (e.g. the per-run workspace path) are normalized via `scrub` before hashing
 * so an unchanged flow matches across runs; `rewriteCwd` rewrites the recorded workspace path in
 * replayed responses so recorded absolute tool-call paths land in the replay workspace.
 *
 * Test-only: exported via `@robota-sdk/agent-core/testing`; never imported by runtime code.
 */

import { createHash } from 'node:crypto';
import { readFileSync, writeFileSync } from 'node:fs';

import type { IAIProvider, IChatOptions, IToolCall, TUniversalMessage } from '../index.js';

const SCRUB_TOKEN = ' SCRUBBED ';
const CASSETTE_RECORD_CWD = ' RECORD_CWD ';

/** One recorded request→response interaction. */
export interface ICassetteInteraction {
  requestHash: string;
  requestPreview: string;
  response: TUniversalMessage;
}

/** A recorded session: an ordered list of interactions plus metadata. */
export interface ICassette {
  version: 1;
  provider: string;
  model?: string;
  /** Recorded workspace path, stored as a placeholder so replay can rewrite it. */
  recordCwd?: string;
  interactions: ICassetteInteraction[];
}

function applyScrub(text: string, scrub: readonly string[]): string {
  let out = text;
  for (const value of scrub) {
    if (value) out = out.split(value).join(SCRUB_TOKEN);
  }
  return out;
}

/**
 * Normalized, volatile-free projection of a request used for the staleness hash.
 *
 * System messages are excluded: the system prompt embeds host/version-specific content (platform,
 * paths, tool inventory) that differs across machines, so hashing it would make a cassette
 * non-portable (a stale failure in CI for a run recorded on another host). The portable, meaningful
 * content — the user/goal prompts, the assistant tool calls, and the tool results — is retained, so
 * a change to the goal prompts still trips staleness.
 */
function normalizeRequest(messages: TUniversalMessage[], scrub: readonly string[]): string {
  const projection = messages
    .filter((message) => message.role !== 'system')
    .map((message) => {
      const toolCalls =
        'toolCalls' in message ? (message.toolCalls as IToolCall[] | undefined) : undefined;
      return {
        role: message.role,
        content:
          typeof message.content === 'string'
            ? applyScrub(message.content, scrub)
            : (message.content ?? null),
        toolCalls:
          toolCalls?.map((call) => ({
            name: call.function?.name ?? null,
            args: call.function?.arguments ? applyScrub(call.function.arguments, scrub) : null,
          })) ?? null,
      };
    });
  return JSON.stringify(projection);
}

function hashRequest(messages: TUniversalMessage[], scrub: readonly string[]): string {
  return createHash('sha256').update(normalizeRequest(messages, scrub)).digest('hex').slice(0, 16);
}

function previewRequest(messages: TUniversalMessage[], scrub: readonly string[]): string {
  const last = messages[messages.length - 1];
  const content = typeof last?.content === 'string' ? applyScrub(last.content, scrub) : '';
  return `${messages.length} msg(s); last ${last?.role ?? '?'}: ${content.slice(0, 80)}`;
}

/** Forward the non-`chat` surface of a wrapped provider unchanged. */
function delegate(provider: IAIProvider): Omit<IAIProvider, 'chat' | 'name' | 'version'> {
  return {
    generateResponse: provider.generateResponse.bind(provider),
    supportsTools: provider.supportsTools.bind(provider),
    validateConfig: provider.validateConfig.bind(provider),
  };
}

export interface IRecordingProviderOptions {
  provider: IAIProvider;
  cassettePath: string;
  /** Volatile substrings normalized out of the staleness hash (e.g. the workspace path). */
  scrub?: readonly string[];
  /** Recorded workspace path; replay rewrites it into the replay workspace. */
  recordCwd?: string;
  model?: string;
}

/** Wrap a real provider, recording every interaction to the cassette file as it runs. */
export function createRecordingProvider(options: IRecordingProviderOptions): IAIProvider {
  const scrub = options.scrub ?? (options.recordCwd ? [options.recordCwd] : []);
  const interactions: ICassetteInteraction[] = [];

  const flush = (): void => {
    const cassette: ICassette = {
      version: 1,
      provider: options.provider.name,
      ...(options.model !== undefined ? { model: options.model } : {}),
      ...(options.recordCwd !== undefined ? { recordCwd: options.recordCwd } : {}),
      interactions,
    };
    writeFileSync(options.cassettePath, `${JSON.stringify(cassette, null, 2)}\n`, 'utf8');
  };

  return {
    name: options.provider.name,
    version: options.provider.version,
    async chat(
      messages: TUniversalMessage[],
      chatOptions?: IChatOptions,
    ): Promise<TUniversalMessage> {
      const response = await options.provider.chat(messages, chatOptions);
      // Store the raw response; the record workspace path is rewritten at replay time, not here.
      interactions.push({
        requestHash: hashRequest(messages, scrub),
        requestPreview: previewRequest(messages, scrub),
        response,
      });
      flush();
      return response;
    },
    ...delegate(options.provider),
  };
}

function reviveResponse(
  response: TUniversalMessage,
  rewriteCwd: string | undefined,
  recordCwd: string | undefined,
): TUniversalMessage {
  let revived = response;
  if (rewriteCwd && recordCwd && rewriteCwd !== recordCwd) {
    revived = JSON.parse(
      JSON.stringify(revived).split(recordCwd).join(rewriteCwd),
    ) as TUniversalMessage;
  }
  // The timestamp round-trips through JSON as a string; the loop expects a Date.
  return { ...revived, timestamp: new Date(revived.timestamp as unknown as string) };
}

export interface IReplayProviderOptions {
  cassettePath: string;
  /** Volatile substrings normalized out of the staleness hash (must match the record-time scrub). */
  scrub?: readonly string[];
  /** Replay workspace path; the recorded workspace path in responses is rewritten to it. */
  rewriteCwd?: string;
}

/** Replay a recorded cassette deterministically, failing clearly on staleness or exhaustion. */
export function createReplayProvider(options: IReplayProviderOptions): IAIProvider {
  const cassette = JSON.parse(readFileSync(options.cassettePath, 'utf8')) as ICassette;
  // Record-time hashing scrubbed the record workspace path → SCRUB_TOKEN; replay must scrub the
  // replay workspace path the same way so an unchanged flow matches across the two runs.
  const scrub = options.scrub ?? (options.rewriteCwd ? [options.rewriteCwd] : []);
  let cursor = 0;

  return {
    name: 'cassette-replay-provider',
    version: 'test',
    async chat(messages: TUniversalMessage[]): Promise<TUniversalMessage> {
      const interaction = cassette.interactions[cursor];
      if (!interaction) {
        throw new Error(
          `Cassette exhausted at call ${cursor + 1} (recorded ${cassette.interactions.length}) — re-record ${options.cassettePath}`,
        );
      }
      const incoming = hashRequest(messages, scrub);
      if (incoming !== interaction.requestHash) {
        throw new Error(
          `Cassette stale at call ${cursor + 1}: the request changed since recording.\n` +
            `  recorded: ${interaction.requestPreview}\n` +
            `  current : ${previewRequest(messages, scrub)}\n` +
            `Re-record ${options.cassettePath}.`,
        );
      }
      cursor += 1;
      return reviveResponse(interaction.response, options.rewriteCwd, cassette.recordCwd);
    },
    async generateResponse() {
      return { content: 'cassette replay does not implement raw responses' };
    },
    supportsTools() {
      return true;
    },
    validateConfig() {
      return true;
    },
  };
}
